import { httpRequest } from '../../shared/lib/utils/httpClient';
import { rowsPerPage } from '../../pages/constants';
import type { DynamicForm } from '@dynamicForm/FieldConfig';
import { PORTAL_TYPES } from '@qler-view/shared_lib/src';
import { type ConsultResponse } from '../hooks/ConsultsHook';

export interface FacilityList {
  id: number;
  name: string;
}

export interface CreateStatusList {
  id: number;
  name: string;
}

export interface UpdateStatusList {
  id: number;
  name: string;
}

export interface AssociatedCliniciansByFacility {
  id: number;
  name: string;
}

export interface ConsultsMetaData {
  facilityList: FacilityList[];
  createStatusList: CreateStatusList[];
  updateStatusList: UpdateStatusList[];
}

export interface Consult {
  id: number;
  sessionId: string;
  createdTime: string;
  calculatedWaitTime: string;
  totalWaitTime: string;
  facilityName: string;
  patientFirstName: string;
  patientLastName: string;
  patientMrn: string;
  dateOfBirth: string;
  status: string;
  assignedClinician: string;
  unreadMessageCount: number;
  emergencyDeptOrInPatient: string;
  followUp: string;
}

export interface ConsultsList {
  totalRecords: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  consultsList: Consult[];
}

export interface SessionFields {
  patientFirstName: string;
  patientLastName: string;
  dateOfBirth?: string;
  patientMrn: string;
  callBackNumber: string;
  reasonForConsult: string;
  unitOrDepartment: string;
  referringProvider: string;
  cart: string;
}

export interface AdditionalSessionFields {
  id: number;
  fieldName: string;
  fieldValue: string | number;
}

export interface ViewDynamicFields {
  id: number;
  fieldName: string;
  fieldLabel: string;
  fieldValue: string | number;
}

export interface ConsultObservation {
  recommendation: string;
  diagnosis: string;
  emCodes: string;
  isBillableConsult: boolean;
  isSurge: boolean;
  opsComments: string;
  unitOrDepartment: string;
  followUp: string;
}

export interface ConsultForm {
  id: number;
  facilityId: number;
  sessionStatusId: number;
  sessionId: string;
  consultAssignedToProviderId: number;
  sessionFields: SessionFields;
  additionalSessionFields: AdditionalSessionFields[];
  associatedCliniciansByFacility?: AssociatedCliniciansByFacility[];
  consultObservation?: ConsultObservation;
  notes: string;
}

export interface ConsultView {
  id: number;
  facilityName: string;
  sessionStatus: string;
  sessionId: string;
  sessionFields: ViewDynamicFields[];
  additionalSessionFields: ViewDynamicFields[];
  consultAssignedToProviderId: number | null;
  unreadMessageCount: number;
}

export interface CancelReasonList {
  id: number;
  name: string;
}

export interface CancelTechIssueList {
  id: number;
  name: string;
}

export interface ConsultCancelMetaData {
  cancelReasonList: CancelReasonList[];
  cancelTechIssueList: CancelTechIssueList[];
}

export interface CancelReasonOpsParams {
  cancelReasonId: number;
  technicalDifficultiesId: number;
  otherReason: string;
}

export interface ConsultCancelReason {
  cancelReasonId: number;
  technicalDifficultiesId: number;
  otherReason: string;
}

export interface ConsultsListParams {
  page?: number;
  size?: number;
  sortField?: string;
  sortType?: string;
  searchTerm?: string;
  statusId?: number[];
  facilityId?: number[];
  followUpId?: number[];
}

export const getConsultsList = async ({
  page = 0,
  size = rowsPerPage,
  sortField,
  sortType,
  searchTerm,
  statusId,
  facilityId,
  followUpId,
}: ConsultsListParams): Promise<ConsultsList> => {
  const params: Record<string, string | number> = {
    page,
    size,
  };
  const userId = localStorage.getItem('userId') ?? '0';

  if (sortField) params.sortField = sortField;
  if (sortType) params.sortType = sortType;
  if (searchTerm) params.searchTerm = searchTerm;
  if (statusId && statusId.length > 0) params.statusId = statusId.join(',');
  if (facilityId && facilityId.length > 0)
    params.facilityId = facilityId.join(',');
  if (followUpId && followUpId.length > 0)
    params.followUpId = followUpId.join(',');
  const response = await httpRequest<ConsultsList>({
    method: 'GET',
    url: '/operation/consults',
    params,
    headers: { userId },
  });
  return response;
};

export const getConsultsMetaData = async (
  userId: string,
  consultId: number
): Promise<ConsultsMetaData> => {
  const response = await httpRequest<ConsultsMetaData>({
    method: 'GET',
    url: `/operation/consult/${consultId}/metadata`,
    headers: { userId },
  });
  return response;
};

export const getIntakeFormForConsult = async (
  userId: string,
  facilityId: number,
  consultId?: number
): Promise<DynamicForm> => {
  let url = `/integration/consult/intake-form?facilityId=${facilityId}`;

  if (consultId !== undefined) {
    url += `&consultId=${consultId}`;
  }

  const response = await httpRequest<DynamicForm>({
    method: 'GET',
    url,
    headers: { userId, portalType: PORTAL_TYPES.OPERATIONS },
    apiBaseType: 'INTEGRATION',
  });

  return response;
};

export const saveOrUpdateConsultsForm = async (
  userId: string,
  action: string,
  payload: ConsultForm
) => {
  const apiAction = action === 'Save' ? 'SAVE' : 'UPDATE';
  const response = await httpRequest<ConsultResponse>({
    method: 'PUT',
    url: `/operation/consult?action=${apiAction}`,
    headers: { userId },
    data: payload,
  });
  return response;
};

export const getConsultDetailsById = async (
  userId: string,
  consultId: number
): Promise<ConsultForm> => {
  const response = await httpRequest<ConsultForm>({
    method: 'GET',
    url: `/operation/consult?consultId=${consultId}`,
    headers: { userId },
  });
  return response;
};

export const getConsultViewDetailsById = async (
  userId: string,
  consultId: number
): Promise<ConsultView> => {
  const response = await httpRequest<ConsultView>({
    method: 'GET',
    url: `/operation/consult/view?consultId=${consultId}`,
    headers: { userId },
  });
  return response;
};

export const getConsultCancelMetaData = async (
  userId: string
): Promise<ConsultCancelMetaData> => {
  const response = await httpRequest<ConsultCancelMetaData>({
    method: 'GET',
    url: `integration/consult/cancel/metadata`,
    headers: { userId, portalType: PORTAL_TYPES.OPERATIONS },
    apiBaseType: 'INTEGRATION',
  });
  return response;
};

export const saveCancelConsult = async (
  userId: string,
  consultId: number,
  payload: ConsultCancelReason
) => {
  const response = await httpRequest<ConsultCancelReason>({
    method: 'PUT',
    url: `/integration/consult/${consultId}/cancel`,
    headers: { userId, portalType: PORTAL_TYPES.OPERATIONS },
    data: payload,
    apiBaseType: 'INTEGRATION',
  });
  return response;
};
